/**
 * Error Logging Configuration
 *
 * Provides environment-aware configuration for error logging strategies.
 * In development: Logs to console with detailed information
 * In production: Logs to Sentry with sanitized data
 */

export interface ErrorLoggingConfig {
  environment: 'development' | 'production' | 'test';
  enableConsoleLogging: boolean;
  enableSentryLogging: boolean;
  logLevel: 'error' | 'warn' | 'info' | 'debug';
  maxBreadcrumbs: number;
  sanitizeData: boolean;
  enablePerformanceLogging: boolean;
  maxErrorsPerMinute: number;
}

/**
 * Gets the current error logging configuration based on environment
 */
export const getErrorLoggingConfig = (): ErrorLoggingConfig => {
  const isDev = import.meta.env.DEV;
  const isProd = import.meta.env.PROD;
  const isTest = import.meta.env.MODE === 'test';

  return {
    environment: isDev ? 'development' : isProd ? 'production' : 'test',
    enableConsoleLogging: isDev || isTest,
    enableSentryLogging: isProd,
    logLevel: isDev ? 'debug' : 'error',
    maxBreadcrumbs: isDev ? 100 : 20,
    sanitizeData: isProd,
    enablePerformanceLogging: isDev,
    maxErrorsPerMinute: isDev ? 50 : 10, // Higher rate limit in dev for debugging
  };
};

/**
 * Sensitive data patterns to filter out
 */
export const SENSITIVE_DATA_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /api[_-]?key/i,
  /auth/i,
  /credential/i,
  /csrf/i,
  /session/i,
];

/**
 * URL parameters to sanitize
 */
export const SENSITIVE_URL_PARAMS = [
  'password',
  'token',
  'api_key',
  'auth_token',
  'session_id',
  'csrf_token',
];
